import React, { useEffect, useMemo, useState } from 'react';
import AdminDashboardScreen, { AdminDashboardTile } from './AdminDashboardScreen';
import AdminLayout from './AdminLayout';
import AdminLoginScreen from './AdminLoginScreen';
import AdminResearchDeskScreen from './AdminResearchDeskScreen';
import AdminResearchChartsScreen from './AdminResearchChartsScreen';
import AdminResearchInsightsScreen from './AdminResearchInsightsScreen';
import AdminValuationScreen from './AdminValuationScreen';
import AdminStockHistoryScreen from './AdminStockHistoryScreen';
import AdminAlertsScreen from './AdminAlertsScreen';
import AdminNotificationsScreen from './AdminNotificationsScreen';
import AdminWatchlistsScreen from './AdminWatchlistsScreen';
import AdminPortfoliosScreen from './AdminPortfoliosScreen';
import AdminBugReportsScreen from './AdminBugReportsScreen';
import AdminUsersScreen from './AdminUsersScreen';
import AdminTasksScreen from './AdminTasksScreen';
import { AdminPage, AdminSection } from './AdminSidebar';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';
import { config } from '../../config/config';

interface AdminShellProps {
  isDarkMode: boolean;
}

const TOKEN_KEY = 'admin:token';
const USERNAME_KEY = 'admin:username';
const EXPIRES_KEY = 'admin:expiresAtUtc';
const MAIN_APP_AUTH_KEY = 'garp_admin_auth_v1';
const SESSION_WARNING_MS = 2 * 60 * 1000;

type JwtRole = 'Admin' | 'User' | 'Unknown';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64Url = parts[1];
    const b64 = b64Url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    const padded = pad === 0 ? b64 : (pad === 2 ? b64 + '==' : pad === 3 ? b64 + '=' : b64);
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function getJwtRole(token: string): JwtRole {
  const payload = decodeJwtPayload(token);
  if (!payload) return 'Unknown';

  const roleClaim =
    payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
    payload['role'] ??
    payload['roles'];

  if (typeof roleClaim === 'string') {
    return roleClaim.toLowerCase() === 'admin' ? 'Admin' : 'User';
  }
  if (Array.isArray(roleClaim)) {
    return roleClaim.some((r) => typeof r === 'string' && r.toLowerCase() === 'admin') ? 'Admin' : 'User';
  }

  return 'User';
}

const AdminShell: React.FC<AdminShellProps> = ({ isDarkMode }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);
  const administratorRouteEnabled = config.ui?.enableAdministratorRoute === true;

  const hasMainAppAdminLogin = useMemo(() => {
    if (!administratorRouteEnabled) return false;
    try {
      const raw = window.localStorage.getItem(MAIN_APP_AUTH_KEY);
      if (!raw) return false;

      const parsed = JSON.parse(raw) as { token?: string; expiresAtUtc?: string };
      const token = String(parsed?.token ?? '').trim();
      if (!token) return false;

      const expiresMs = new Date(String(parsed?.expiresAtUtc ?? '')).getTime();
      if (!Number.isFinite(expiresMs) || expiresMs <= Date.now()) return false;

      return getJwtRole(token) === 'Admin';
    } catch {
      return false;
    }
  }, [administratorRouteEnabled]);
  const [token, setToken] = useState<string | null>(() => {
    try {
      const t = window.localStorage.getItem(TOKEN_KEY);
      return t && t.trim().length > 0 ? t : null;
    } catch {
      return null;
    }
  });
  const [expiresAtUtc, setExpiresAtUtc] = useState<string | null>(() => {
    try {
      const e = window.localStorage.getItem(EXPIRES_KEY);
      return e && e.trim().length > 0 ? e : null;
    } catch {
      return null;
    }
  });

  const [role, setRole] = useState<JwtRole>(() => (token ? getJwtRole(token) : 'Unknown'));
  const [showSessionExpiryWarning, setShowSessionExpiryWarning] = useState(false);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState<number | null>(null);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  useEffect(() => {
    setRole(token ? getJwtRole(token) : 'Unknown');
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const expiresMs = new Date(String(expiresAtUtc ?? '')).getTime();
    if (!Number.isFinite(expiresMs)) return;

    const remainingMs = expiresMs - Date.now();
    if (remainingMs <= 0) {
      logout();
      return;
    }

    setShowSessionExpiryWarning(remainingMs <= SESSION_WARNING_MS);
    setSessionSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));

    const timer = window.setTimeout(() => {
      logout();
    }, remainingMs);

    const warningTimer =
      remainingMs > SESSION_WARNING_MS
        ? window.setTimeout(() => {
            setShowSessionExpiryWarning(true);
          }, remainingMs - SESSION_WARNING_MS)
        : null;

    const tickTimer = window.setInterval(() => {
      const leftMs = expiresMs - Date.now();
      if (leftMs <= 0) {
        setSessionSecondsLeft(0);
        return;
      }
      setSessionSecondsLeft(Math.ceil(leftMs / 1000));
    }, 1000);

    return () => {
      window.clearTimeout(timer);
      if (warningTimer !== null) {
        window.clearTimeout(warningTimer);
      }
      window.clearInterval(tickTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, expiresAtUtc]);

  const [dashboardStats, setDashboardStats] = useState<{
    userRegistrations: number;
    activeUsers: number;
    bugReports: number;
  } | null>(null);
  const [dashboardStatsError, setDashboardStatsError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    if (role !== 'Admin') return;
    let cancelled = false;

    (async () => {
      try {
        setDashboardStatsError(null);
        const stats = await adminApi.getDashboardStats(token);
        if (cancelled) return;
        setDashboardStats(stats);
      } catch (err) {
        if (cancelled) return;
        if (isUnauthorized(err)) {
          logout();
          return;
        }
        setDashboardStatsError(err instanceof Error ? err.message : 'Failed loading dashboard stats');
        setDashboardStats(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role]);

  const tiles: AdminDashboardTile[] = useMemo(() => {
    const bugReportsValue = dashboardStatsError ? '—' : (dashboardStats == null ? '…' : String(dashboardStats.bugReports));
    const userRegistrationsValue = dashboardStatsError ? '—' : (dashboardStats == null ? '…' : String(dashboardStats.userRegistrations));
    const activeUsersValue = dashboardStatsError ? '—' : (dashboardStats == null ? '…' : String(dashboardStats.activeUsers));
    return [
      { id: 'blue', title: 'New Orders', value: '0', subtitle: '' },
      { id: 'green', title: 'Bug Reports', value: bugReportsValue, subtitle: dashboardStatsError ? 'Failed to load' : 'From bugs table' },
      { id: 'yellow', title: 'User Registrations', value: userRegistrationsValue, subtitle: dashboardStatsError ? 'Failed to load' : 'From users table' },
      { id: 'red', title: 'Active Users', value: activeUsersValue, subtitle: dashboardStatsError ? 'Failed to load' : 'Currently logged in' },
    ];
  }, [dashboardStats, dashboardStatsError]);

  const [expandedSections, setExpandedSections] = useState<Record<AdminSection, boolean>>({
    Dashboard: true,
    Users: false,
    Protfolio: true,
    Setting: false,
  });

  const [activePage, setActivePage] = useState<AdminPage>('Dashboard');

  const logout = () => {
    const currentToken = token;
    if (currentToken) {
      // Best-effort server logout (JWT is stateless; primary action is clearing the token).
      void adminApi.logout(currentToken).catch(() => {
        // ignore
      });
    }
    try {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(USERNAME_KEY);
      window.localStorage.removeItem(EXPIRES_KEY);
    } catch {
      // ignore
    }
    setToken(null);
    setExpiresAtUtc(null);
    setShowSessionExpiryWarning(false);
    setSessionSecondsLeft(null);
    setIsRefreshingSession(false);
    setActivePage('Dashboard');
  };

  const formatSessionCountdown = (totalSeconds: number | null): string => {
    const safe = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(Number(totalSeconds))) : 0;
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const extendSession = async () => {
    if (!token || isRefreshingSession) return;
    try {
      setIsRefreshingSession(true);
      const refreshed = await adminApi.refresh(token);
      try {
        window.localStorage.setItem(TOKEN_KEY, refreshed.token);
        window.localStorage.setItem(USERNAME_KEY, refreshed.username);
        window.localStorage.setItem(EXPIRES_KEY, refreshed.expiresAtUtc);
      } catch {
        // ignore
      }
      setToken(refreshed.token);
      setExpiresAtUtc(refreshed.expiresAtUtc);
      setShowSessionExpiryWarning(false);
    } catch (err) {
      if (isUnauthorized(err)) {
        logout();
        return;
      }
    } finally {
      setIsRefreshingSession(false);
    }
  };

  // Feature gate: when disabled, /administrator must render blank for everyone.
  if (!administratorRouteEnabled) {
    return <div style={{ minHeight: '100vh', background: 'transparent' }} />;
  }

  // Allow seeing the admin login screen only if user already logged into main app as Admin.
  if (!hasMainAppAdminLogin) {
    return <div style={{ minHeight: '100vh', background: 'transparent' }} />;
  }

  if (!token) {
    return (
      <AdminLoginScreen
        isDarkMode={isDarkMode}
        onLogin={async (username, password) => {
          const res = await adminApi.login(username, password);
          try {
            window.localStorage.setItem(TOKEN_KEY, res.token);
            window.localStorage.setItem(USERNAME_KEY, res.username);
            window.localStorage.setItem(EXPIRES_KEY, res.expiresAtUtc);
          } catch {
            // ignore
          }
          setToken(res.token);
          setExpiresAtUtc(res.expiresAtUtc);
        }}
      />
    );
  }

  // The /administrator console is admin-only. If a non-admin logs in (e.g. alonh),
  // show a clear message instead of attempting admin-only API calls (which return 403).
  if (role !== 'Admin') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDarkMode ? '#0b1220' : '#f6f7fb',
          padding: 16,
        }}
      >
        <div
          style={{
            width: 'min(520px, 100%)',
            backgroundColor: theme.cardBg,
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radius,
            padding: 18,
            color: theme.text,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Admin access required</div>
          <div style={{ color: theme.subText, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
            You are logged in, but <span style={{ fontFamily: 'monospace' }}>/administrator</span> is available to Admin users only.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                window.location.href = '/';
              }}
              style={{
                borderRadius: 10,
                padding: '10px 12px',
                border: `1px solid ${theme.border}`,
                background: theme.primary,
                color: '#fff',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Go to app
            </button>
            <button
              onClick={logout}
              style={{
                borderRadius: 10,
                padding: '10px 12px',
                border: `1px solid ${theme.border}`,
                background: theme.cardBg,
                color: theme.text,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pageTitle = activePage;
  const pageTitleText = activePage === 'Tasks' ? 'Scheduled Tasks' : pageTitle;
  const breadcrumb = `Home / ${pageTitleText}`;

  const mainContent =
    activePage === 'Dashboard' ? (
      <AdminDashboardScreen isDarkMode={isDarkMode} tiles={tiles} />
    ) : activePage === 'Users' ? (
      <AdminUsersScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Stock History' ? (
      <AdminStockHistoryScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Alerts' ? (
      <AdminAlertsScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Notifications' ? (
      <AdminNotificationsScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Watchlists' ? (
      <AdminWatchlistsScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Portfolios' ? (
      <AdminPortfoliosScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Bug Reports' ? (
      <AdminBugReportsScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Tasks' ? (
      <AdminTasksScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Research Desk' ? (
      <AdminResearchDeskScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Research Charts' ? (
      <AdminResearchChartsScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : activePage === 'Research Insights' ? (
      <AdminResearchInsightsScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
      ) : activePage === 'Valuation' ? (
        <AdminValuationScreen isDarkMode={isDarkMode} token={token} onUnauthorized={logout} />
    ) : (
      <div
        style={{
          backgroundColor: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: theme.radius,
          padding: 18,
          color: theme.subText,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        Coming soon
      </div>
    );

  return (
    <AdminLayout
      isDarkMode={isDarkMode}
      title={pageTitleText}
      breadcrumb={breadcrumb}
      onLogout={logout}
      sidebar={{
        expanded: expandedSections,
        activePage,
        onToggleSection: (section) =>
          setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
          })),
        onSelectPage: (page) => setActivePage(page),
      }}
    >
      {showSessionExpiryWarning && (
        <div
          style={{
            marginBottom: 12,
            background: isDarkMode ? '#2a1f12' : '#fff4db',
            border: `1px solid ${isDarkMode ? '#7a5a2d' : '#f1cf8f'}`,
            borderRadius: 10,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ color: theme.text, fontSize: 13, fontWeight: 700 }}>
            Your admin session will expire in {formatSessionCountdown(sessionSecondsLeft)}.
          </div>
          <button
            onClick={extendSession}
            disabled={isRefreshingSession}
            style={{
              borderRadius: 8,
              padding: '8px 10px',
              border: `1px solid ${theme.border}`,
              background: theme.primary,
              color: '#fff',
              fontWeight: 800,
              fontSize: 12,
              cursor: isRefreshingSession ? 'default' : 'pointer',
              opacity: isRefreshingSession ? 0.75 : 1,
            }}
          >
            {isRefreshingSession ? 'Extending...' : 'Extend session'}
          </button>
        </div>
      )}
      {mainContent}
    </AdminLayout>
  );
};

export default AdminShell;
